import Page from "@/components/layout/Page";
import * as Styled from "./styled";
import DocLayout from "@/components/layout/DocLayout";
import useApi from "@/contexts/ApiContext";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import DatoBlockRenderer from "@/components/shared-ui/DatoBlockRenderer";
import { Download } from "@/components/layout/DocLayout/styled";
import { Badge } from "../Dashboard/UpdatesCard/styled";
import useDocsActiveHeading from "@/contexts/DocsActiveHeadingContext";
import { AnchorItem } from "../Documentation/styled";
import useCompany from "@/contexts/CompanyContext";
import PlainDocIcon from "@assets/icons/plain-doc-icon.svg?react";
import { UPDATE_CATEGORY_LABELS } from "../Updates/updateConstants";
import axios from "axios";
import NotFound from "../NotFound";

const Update = () => {
  const api = useApi();
  const { companyStatus } = useCompany();
  const { updateSlug } = useParams();
  const { activeId } = useDocsActiveHeading();
  const location = useLocation();
  const { prevLocation } = location.state || {};
  const { data: update, error } = useQuery({
    queryKey: ["get-update", companyStatus, updateSlug],
    queryFn: () => api.update.get(companyStatus, updateSlug || ""),
    retry: (failureCount, queryError) =>
      !(
        axios.isAxiosError(queryError) && queryError.response?.status === 404
      ) && failureCount < 3,
    throwOnError: (queryError) =>
      !(
        axios.isAxiosError(queryError) && queryError.response?.status === 404
      ),
  });

  const { data: updates } = useQuery({
    queryKey: ["get-updates", companyStatus],
    queryFn: () => api.update.getAll(companyStatus, 3),
    throwOnError: false,
  });

  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return <NotFound />;
  }

  if (!update) {
    return <LoadingScreen />;
  }

  // Extract h3 headings from StructuredText DAST
  const levelThreeHeadings: string[] = [];
  if (update.content?.value) {
    const doc = update.content.value as { document?: { children?: unknown[] } };
    const walk = (nodes: unknown[]) => {
      for (const node of nodes) {
        const n = node as { type?: string; level?: number; children?: unknown[] };
        if (n.type === "heading" && n.level === 3 && Array.isArray(n.children)) {
          const text = (n.children as { value?: string }[]).map((c) => c.value ?? "").join("");
          if (text) levelThreeHeadings.push(text);
        }
        if (Array.isArray(n.children)) walk(n.children);
      }
    };
    if (doc.document?.children) walk(doc.document.children);
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sidebar = (
    <>
      {levelThreeHeadings.length > 0 && (
        <Styled.AnchorHeading>On this page</Styled.AnchorHeading>
      )}
      <div>
        {levelThreeHeadings.map((heading) => {
          const anchorSuitableId = heading.replace(/\s+/g, "-").toLowerCase();
          return (
            <AnchorItem
              key={anchorSuitableId}
              href={`#${anchorSuitableId}`}
              $active={activeId === anchorSuitableId}
            >
              {heading}
            </AnchorItem>
          );
        })}
      </div>

      {update.download && (
        <>
          <Styled.AnchorHeading>Download</Styled.AnchorHeading>
          <Download>
            <a href={update.download.url} download={update.download.filename}>
              <PlainDocIcon />
            </a>
            {update.download.filename}
          </Download>
        </>
      )}
      {updates && updates.length > 0 && (
        <>
          <Styled.AnchorHeading>Recent notes</Styled.AnchorHeading>
          <div>
            {updates.map((item) => (
              <Link to={`/updates/${item.slug}`} key={item.id}>
                <Styled.RecentUpdatesItem $category={item.category}>
                  <span>{item.title}</span>
                  <Styled.Date>{formatDate(item.date)}</Styled.Date>
                </Styled.RecentUpdatesItem>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );

  const docPrefix = (
    <Styled.RecentUpdatesItem $category={update.category}>
      <Badge>
        {UPDATE_CATEGORY_LABELS[update.category] ?? update.category}
      </Badge>
      <Styled.Date>{formatDate(update.date)}</Styled.Date>
    </Styled.RecentUpdatesItem>
  );

  const breadcrumb = prevLocation
    ? { label: "Back to roadmap", path: prevLocation }
    : { label: "Back to updates", path: "/updates" };

  return (
    <Page
      title="Update"
      customBreadcrumb={breadcrumb}
      noPadding
      fullWidth
      stickyBreadcrumbs
      searchIndexPriority="updates"
      whiteBackground
    >
      <DocLayout
        primarySidebar={{
          visible: false,
          heading: "",
          menu: <></>,
          accordionHeading: "",
        }}
        secondarySidebar={{
          visible: true,
          menu: sidebar,
        }}
        docPrefix={docPrefix}
        docHeading={update.title}
        typographyStyle="update"
        docContent={<DatoBlockRenderer content={update.content} />}
      />
    </Page>
  );
};

export default Update;
